import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { CATEGORIES } from "@/data/site";
import { EASE, SectionHead } from "@/components/motion/Reveal";
import { cn } from "@/lib/utils";
import { ParallaxImg } from "@/components/fx/Motion";

const SPANS = ["lg:col-span-7 lg:row-span-2", "lg:col-span-5", "lg:col-span-5", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4"];

const trackSpot = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

const BentoCard = ({ c, i }) => (
  <motion.article
    initial={{ clipPath: "inset(14% 10% 14% 10% round 28px)", opacity: 0 }}
    whileInView={{ clipPath: "inset(0% 0% 0% 0% round 28px)", opacity: 1 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 1.2, ease: EASE, delay: (i % 3) * 0.08 }}
    onMouseMove={trackSpot}
    className={cn("group relative min-h-[340px] overflow-hidden rounded-[28px] border border-white/5 bg-ink-800", SPANS[i])}
    data-testid={`category-card-${c.slug}`}
  >
    <ParallaxImg src={c.img} alt={c.title} className="opacity-80 transition-transform duration-[1.4s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]" />
    <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/55 to-ink-950/10" />
    <div className="spot absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
    <div className="relative flex h-full flex-col justify-between p-6 sm:p-8">
      <div className="flex items-start justify-between">
        <span className="font-mono text-xs text-volt">0{i + 1}</span>
        <div className="flex flex-wrap justify-end gap-2">
          {c.tags.map((t) => (
            <span key={t} className="rounded-full border border-white/15 bg-ink-950/50 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-300 backdrop-blur-md">{t}</span>
          ))}
        </div>
      </div>
      <div>
        <h3 className={cn("font-display font-bold tracking-tight text-white", i === 0 ? "text-3xl sm:text-5xl" : "text-2xl sm:text-3xl")}>{c.title}</h3>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-300">{c.desc}</p>
        <Link to={`/equipment?category=${c.slug}`} className="btn-ghost mt-6 h-11 px-5 text-sm backdrop-blur-md" data-testid={`explore-equipment-${c.slug}`}>
          Explore Equipment <ArrowRight className="btn-arrow h-4 w-4" />
        </Link>
      </div>
    </div>
  </motion.article>
);

export const CategoryBento = ({ index = "01" }) => (
  <section id="equipment" className="relative py-28 lg:py-40" data-testid="equipment-categories-section">
    <div className="container-x">
      <SectionHead index={index} eyebrow="Equipment" title={["Equipment built for", "commercial floors."]}>
        Six core categories, sourced and set up for real-world traffic. Pick a category to see what fits your location.
      </SectionHead>
      <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:auto-rows-[300px] lg:grid-cols-12">
        {CATEGORIES.map((c, i) => (
          <BentoCard key={c.slug} c={c} i={i} />
        ))}
      </div>
    </div>
  </section>
);
